import { zodResolver } from '@hookform/resolvers/zod'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  AddOrgFavouriteByArticleError,
  useAddOrgFavouriteByArticleApi,
} from 'src/api/db/apiOrgFavourites'
import { FormInput, FormWrapper } from 'src/shared/components/FormComponents'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { Tooltip } from 'src/shared/components/ui/tooltip'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { z } from 'zod'

const formSchema = z.object({
  article_number: z.string().trim().min(1, 'Article number is required'),
})

type FormValues = z.infer<typeof formSchema>

export const AddFavouriteByArticleDialog = ({
  orgId,
  orgName,
}: {
  orgId: string | null | undefined
  orgName?: string | null
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { addByArticle } = useAddOrgFavouriteByArticleApi()
  const [open, setOpen] = useState(false)

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { article_number: '' },
  })

  useEffect(() => {
    if (!open) {
      methods.reset({ article_number: '' })
    }
  }, [open, methods])

  const handleSubmit = async (values: FormValues) => {
    if (!orgId) return

    try {
      await addByArticle.mutateAsync({
        orgId,
        articleNumber: values.article_number,
      })
      toast({
        variant: 'success',
        title: t('Favourite added', 'Favorit tillagd'),
      })
      setOpen(false)
    } catch (error) {
      if (error instanceof AddOrgFavouriteByArticleError) {
        if (error.kind === 'not_found') {
          toast({
            variant: 'destructive',
            title: t(
              'No product found for this article number',
              'Ingen produkt hittades för detta artikelnummer'
            ),
          })
          return
        }
        if (error.kind === 'ambiguous') {
          toast({
            variant: 'destructive',
            title: t(
              'Multiple products match this article number',
              'Flera produkter matchar detta artikelnummer'
            ),
          })
          return
        }
        if (error.kind === 'duplicate') {
          toast({
            variant: 'destructive',
            title: t(
              'This product is already in favourites',
              'Denna produkt finns redan bland favoriter'
            ),
          })
          return
        }
      }
      console.error('Add favourite by article:', error)
      toast({
        variant: 'destructive',
        title: t('Could not add favourite', 'Kunde inte lägga till favorit'),
      })
    }
  }

  const hasOrg = !!orgId

  const addToLabel =
    orgName != null && orgName !== ''
      ? `${t('Add to', 'Lägg till i')} ${orgName}`
      : t(
          'Add favourite by article number',
          'Lägg till favorit med artikelnummer'
        )

  const triggerButton = (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      disabled={!hasOrg}
      className="shrink-0 gap-1.5 max-w-[min(100%,220px)]"
      aria-label={addToLabel}
      onClick={() => setOpen(true)}
    >
      <Plus className="h-4 w-4 shrink-0" />
      {addToLabel}
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hasOrg ? (
        <Tooltip
          content={t(
            'Filter by organization first to choose which org to add to',
            'Filtrera på organisation först för att välja vilken org som ska få favoriten'
          )}
        >
          <span className="inline-flex">{triggerButton}</span>
        </Tooltip>
      ) : (
        triggerButton
      )}

      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t(
              'Add favourite by article number',
              'Lägg till favorit med artikelnummer'
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Enter the product article number to add it to this organization’s favourites.',
              'Ange produktens artikelnummer för att lägga till i denna organisations favoriter.'
            )}
          </DialogDescription>
        </DialogHeader>

        <FormWrapper methods={methods} onSubmit={handleSubmit}>
          <Column className="gap-4">
            <FormInput
              label={t('Article number', 'Artikelnummer')}
              name="article_number"
              methods={methods}
              placeholder={t('e.g. F4900200AJ3', 't.ex. F4900200AJ3')}
            />
            <Row className="justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                {t('Cancel', 'Avbryt')}
              </Button>
              <Button type="submit" disabled={addByArticle.isPending}>
                {t('Add', 'Lägg till')}
              </Button>
            </Row>
          </Column>
        </FormWrapper>
      </DialogContent>
    </Dialog>
  )
}
